import { Button, Spacer } from '@odekoteam/doppio';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import {
  nightEndSelector,
  selectedWarehouseIdSelector,
} from '../../state/selectors/delivery';
import { modalSelector } from '../../state/selectors/modal';
import { unmarkRouteProvider } from '../../state/slices/delivery';
import { hideModal } from '../../state/slices/modal';
import { focusWhenPresent, ROUTE_DETAILS_TITLE_ID } from '../../utils/focus';
import CancelButton from './CancelButton';
import { buttonContainer, styledConfirmationModal } from './styles';

type UnmarkRouteProviderVariables = {
  routeId: string;
  title: string;
  returnFocusId?: string;
  // Where focus lands after a removal; defaults to the route-details title.
  successFocusId?: string;
  // The confirm button's label, for a route the provider already canceled.
  confirmLabel?: string;
};

const TITLE_ID = 'unmark-route-provider-title';
const DESCRIPTION_ID = 'unmark-route-provider-description';

// Confirms taking a route back from a provider. On a quoted route this throws
// away the quotes, and marking again re-prices, so it is never one click.
// Snapshotted at open, like the mark dialog: hideModal clears the store's
// variables while this dialog may still render once.
export function UnmarkRouteProviderModal(): JSX.Element | null {
  const { variables, message } = useAppSelector(modalSelector);
  const [snapshot] = useState<
    (UnmarkRouteProviderVariables & { message?: string }) | undefined
  >(variables ? { ...variables, message } : undefined);
  return snapshot ? <UnmarkRouteProviderDialog {...snapshot} /> : null;
}

function UnmarkRouteProviderDialog({
  routeId,
  title,
  message,
  returnFocusId,
  successFocusId = ROUTE_DETAILS_TITLE_ID,
  confirmLabel = 'Remove',
}: UnmarkRouteProviderVariables & { message?: string }): JSX.Element {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const nightEnd = useAppSelector(nightEndSelector);
  const selectedWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Escape must not close the dialog mid-request, for the reason Cancel is
  // disabled then: the request's own close would hide whatever dialog opened
  // next. A ref, so `cancel` stays stable for the focus trap.
  const submitting = useRef(false);
  const cancel = useCallback(() => {
    if (submitting.current) return;
    dispatch(hideModal());
    if (returnFocusId) document.getElementById(returnFocusId)?.focus();
  }, [dispatch, returnFocusId]);
  useFocusTrap(ref, { isOpen: true, onClose: cancel });
  // Open on Cancel, not on the destructive action the trap would pick first.
  useEffect(() => {
    ref.current
      ?.querySelector<HTMLElement>('#confirmation-modal-dismiss-button')
      ?.focus();
  }, []);

  const handleOnClickConfirm = async (): Promise<void> => {
    if (!selectedWarehouseId || !nightEnd?.viewing) {
      dispatch(hideModal());
      return;
    }
    setIsSubmitting(true);
    submitting.current = true;
    await dispatch(
      unmarkRouteProvider({
        routeId,
        warehouseId: selectedWarehouseId,
        nightEnd: nightEnd.viewing,
      }),
    );
    setIsSubmitting(false);
    submitting.current = false;
    dispatch(hideModal());
    // The control that opened this is gone once the route is an Odeko route
    // again.
    focusWhenPresent(successFocusId);
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      id="unmark-route-provider-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left' }}
    >
      <h2 id={TITLE_ID} sx={{ variant: 'text.headingSm', margin: 0 }}>
        {title}
      </h2>
      <Spacer height="$3" />
      <p
        id={DESCRIPTION_ID}
        sx={{
          variant: 'text.paragraphMdDefault',
          color: 'secondaryText',
          margin: 0,
        }}
      >
        {message}
      </p>
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="remove-from-provider-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting ? 'Removing...' : confirmLabel}
        </Button>
        <Spacer width="$2" />
        <CancelButton
          variant="secondary"
          returnFocusId={returnFocusId}
          isDisabled={isSubmitting}
        />
      </div>
    </div>
  );
}
